import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import {
  Button,
  Form,
  FormSubmitButton,
  SheetFooter,
  SimpleSelectFormField,
  Slideover,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CheckboxFormField } from "@/components/ui/FormFields/CheckboxFormField/CheckboxFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  useCreateContactEmail,
  useUpdateContactEmail,
} from "@/lib/data/react-query";
import { ContactEmailSchema } from "@/lib/data/schemas";
import { getContactEmailLabelOptions } from "@/lib/data/static/contactStatics";
import { type ContactEmail, type ContactType } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { ContactEmailIcon } from "@/lib/utils/iconUtils";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm, useWatch, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

interface AddOrEditContactEmailSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editing: boolean;
  data?: ContactEmail;
  contactId: string;
  onDelete?: () => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  organizationSubTypes?: readonly string[] | null;
  type?: ContactType | null;
  variant?: "slideover" | "inline";
}

type ConditionalProps =
  | {
      editing: true;
      activeContactEmail: Pick<
        ContactEmail,
        "id" | "dateModified" | "address" | "label" | "isPrimary"
      >;
    }
  | {
      editing: false;
      activeContactEmail?: never;
    };

const formSchema = ContactEmailSchema.omit({
  firmId: true,
  dateCreated: true,
  contactId: true,
  id: true,
  description: true,
}).extend({
  address: z.string(),
  isPrimary: z.boolean(),
  label: z.string(),
});

const defaultObject: z.input<typeof formSchema> = {
  address: "",
  label: "",
  dateModified: "",
  isPrimary: false,
};

export const AddOrEditContactEmailSlideover = ({
  open,
  setOpen,
  activeContactEmail,
  onDelete,
  editing,
  contactId,
  hasEditPermission,
  hasDeletePermission,
  parentForm,
  organizationSubTypes,
  type,
  variant = "slideover",
}: AddOrEditContactEmailSlideoverProps & ConditionalProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          address: activeContactEmail.address ?? "",
          label: activeContactEmail.label ?? "",
          isPrimary: activeContactEmail.isPrimary ?? false,
          dateModified: activeContactEmail.dateModified,
        }
      : defaultObject,
    mode: "onSubmit",
  });
  const liveFieldValues = useWatch({ control: form.control });
  const createContactEmail = useCreateContactEmail();
  const updateContactEmail = useUpdateContactEmail();
  const isFormLoading =
    createContactEmail.isPending || updateContactEmail.isPending;

  const onSubmitParentForm = (data: z.infer<typeof formSchema>) => {
    if (!parentForm) return;
    if (editing) {
      const existingEmails = parentForm.getValues("emails") ?? [];
      parentForm.setValue(
        "emails",
        existingEmails.map((email) =>
          email.id === activeContactEmail.id
            ? {
                ...email,
                address: data.address,
                label: data.label,
                isPrimary: data.isPrimary,
                id: activeContactEmail.id,
              }
            : email,
        ),
        { shouldDirty: true },
      );
      return data;
    } else {
      const existingEmails = parentForm.getValues("emails") ?? [];

      parentForm.setValue(
        "emails",
        [
          ...existingEmails,
          {
            address: data.address,
            label: data.label,
            isPrimary: data.isPrimary,
            description: "",
            dateModified: "",
            id: Math.random().toString(),
          },
        ],
        {
          shouldDirty: true,
        },
      );
      return data;
    }
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (parentForm) {
      onSubmitParentForm(data);
      setOpen(false);
      return;
    }
    if (editing) {
      const dirtyKeys = Object.keys(form.formState.dirtyFields) as Array<
        keyof z.infer<typeof formSchema>
      >;
      const dirtyData: Partial<z.infer<typeof formSchema>> = {};
      for (const key of dirtyKeys) {
        if (key === "dateModified") continue;
        (dirtyData as Record<string, unknown>)[key] = data[key];
      }
      const payload: Parameters<typeof updateContactEmail.mutateAsync>[0] = {
        contactId,
        id: activeContactEmail.id,
        dateModified: activeContactEmail.dateModified,
        ...dirtyData,
        // API requires this field on update.
        address: data.address,
      };
      await updateContactEmail.mutateAsync(payload);
    } else {
      await createContactEmail
        .mutateAsync({
          contactId,
          address: data.address,
          label: data.label,
          isPrimary: data.isPrimary,
          dateModified: undefined,
        })
        .then(() => {
          form.reset();
        });
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  /** Keep parent contact form (and live preview) in sync while editing inline — nested fields only flush on submit otherwise. */
  useEffect(() => {
    if (!parentForm || !open || !editing) {
      return;
    }
    const id = activeContactEmail.id;
    const existingEmails = parentForm.getValues("emails") ?? [];
    parentForm.setValue(
      "emails",
      existingEmails.map((email) =>
        email.id === id ? { ...email, ...liveFieldValues, id } : email,
      ),
      {
        shouldDirty: form.formState.isDirty,
        shouldValidate: false,
        shouldTouch: false,
      },
    );
  }, [
    activeContactEmail,
    editing,
    form.formState.isDirty,
    liveFieldValues,
    open,
    parentForm,
  ]);

  const title = editing ? "Update email" : "Create email";
  const isInline = variant === "inline";
  const fieldsetClassName = cn(
    slideoverFieldsetClassName,
    isInline && "p-0 my-2 gap-3",
  );
  const handleInlineSubmit = () => {
    void form.handleSubmit(onSubmit)();
  };
  const formContent = (
    <Form {...form}>
      <form
        className={cn(
          "relative flex grow flex-col",
          isInline ? "overflow-visible" : "overflow-hidden",
        )}
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit(onSubmit)();
        }}
      >
        {isFormLoading && <FormLoadingOverlay />}
        <div
          className={cn(
            "overflow-y-auto",
            variant === "inline" && "overflow-visible",
          )}
        >
          <fieldset
            disabled={editing ? !hasEditPermission : false}
            className={fieldsetClassName}
          >
            <TextFormField
              register={form.register("address")}
              label="Email address*"
              dataTestId="contact-email-address-input"
            />
            <SimpleSelectFormField
              placeholder="Select a label"
              label="Label"
              register={form.register("label")}
              items={getContactEmailLabelOptions(type, organizationSubTypes)}
            />
            <CheckboxFormField
              register={form.register("isPrimary")}
              label="Primary email"
              className={variant === "inline" ? "mt-0 h-auto" : undefined}
            />
          </fieldset>
        </div>
        <div className="flex grow" />
        <SheetFooter addSlideoverStyles={variant === "slideover"}>
          {hasDeletePermission && editing && (
            <Button
              type="button"
              variant="destructive"
              onClick={onDelete}
              data-testid="delete-contact-email-button"
            >
              Delete
            </Button>
          )}
          <div className="flex grow justify-end gap-x-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              type={isInline ? "button" : "submit"}
              submitFn={isInline ? handleInlineSubmit : undefined}
            />
          </div>
        </SheetFooter>
      </form>
    </Form>
  );

  if (variant === "inline") {
    if (!open) return null;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2">
          <ContactEmailIcon className="h-5 w-5 text-gray-400" />
          <div className="space-y-1">
            <Typography weight="medium">{title}</Typography>
          </div>
        </div>
        {formContent}
      </div>
    );
  }

  return (
    <Slideover
      open={open}
      setOpen={setOpen}
      isDirty={form.formState.isDirty}
      title={title}
      isLoading={isFormLoading}
      titleIcon={<ContactEmailIcon className="h-4 w-4" />}
    >
      {formContent}
    </Slideover>
  );
};
